/**
 * Recipe arithmetic for the product form.
 *
 * A note on the guard rail. The dashboard does not compute stock: Stock
 * Levels shows the portions figure the cloud derived, and does not work out
 * its own. This module is the exception the intake editor's `estimateTotals`
 * also is — a PREVIEW of something the cloud cannot have yet, because the
 * recipe being typed does not exist on the server. Once saved, the cloud
 * recomputes and its number is the one everything displays.
 *
 * That is the dividing line: preview where there is no authority yet, defer
 * where there is.
 *
 * Mirrors infrastructure/lambda/stock/ledger.mjs `portionsAvailable`.
 * See docs/feature-stock-management-implementation.md §12.3.
 */

export interface RecipeLine {
  stockId: string;
  qtyPerPortion: number;
  unit?: string;
}

export interface IngredientLike {
  stockId: string;
  itemName: string;
  metricValue: string;
  stockOnHand?: number;
  stockBaselineAt?: string | null;
  stockUpdatedAt?: string | null;
}

/**
 * Units a quantity may be ENTERED in, per ingredient unit.
 *
 * A recipe takes 200 g of rice, not 0.2 kg — people think in the smaller
 * unit. The stored value is always in the ingredient's own unit, because
 * that is what every ledger quantity for it is in.
 */
export const ENTRY_UNITS: Record<string, {unit: string; perBase: number}[]> = {
  kg: [
    {unit: 'g', perBase: 1000},
    {unit: 'kg', perBase: 1},
  ],
  l: [
    {unit: 'ml', perBase: 1000},
    {unit: 'l', perBase: 1},
  ],
};

/** The units this ingredient's quantity may be typed in. */
export function entryUnitsFor(baseUnit?: string): {unit: string; perBase: number}[] {
  return ENTRY_UNITS[baseUnit ?? ''] ?? [{unit: baseUnit ?? '', perBase: 1}];
}

const num = (v: any) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Trim float drift — 200 / 1000 must be 0.2, not 0.20000000000000004. */
const round6 = (n: number) => Number(n.toFixed(6));

/** Convert a typed quantity into the ingredient's own unit. */
export function toBaseQty(value: any, enteredUnit: string, baseUnit?: string): number {
  const option = entryUnitsFor(baseUnit).find((u) => u.unit === enteredUnit);
  if (!option) return round6(num(value));
  return round6(num(value) / option.perBase);
}

/** Convert a stored quantity back into a typed one. */
export function fromBaseQty(value: any, enteredUnit: string, baseUnit?: string): number {
  const option = entryUnitsFor(baseUnit).find((u) => u.unit === enteredUnit);
  if (!option) return round6(num(value));
  return round6(num(value) * option.perBase);
}

/** Has this ingredient ever been counted or received? */
export function isCounted(ingredient?: IngredientLike | null): boolean {
  if (!ingredient) return false;
  return ingredient.stockBaselineAt != null || ingredient.stockUpdatedAt != null;
}

/**
 * Portions one line allows today, or null when the ingredient has never been
 * measured — a line nobody can price in portions is skipped, not zeroed.
 */
export function portionsForLine(
  line: RecipeLine,
  ingredient?: IngredientLike | null,
): number | null {
  const perPortion = num(line?.qtyPerPortion);
  if (!(perPortion > 0)) return null;
  if (!isCounted(ingredient)) return null;
  return Math.floor(num(ingredient!.stockOnHand) / perPortion);
}

export interface RecipePreview {
  tracked: boolean;
  portions: number;
  limitingStockId: string | null;
  limitingName: string | null;
}

/**
 * Collapse a recipe so each ingredient appears once, with its quantities
 * summed.
 *
 * A dish can name the same ingredient twice — rice in the base and rice in the
 * garnish. Treated as two competing lines, the portions figure took the
 * MINIMUM and overstated what could be made. `validateRecipe` rejects
 * duplicates in the editor, but a recipe can also arrive from an import or the
 * API, so the arithmetic has to be right whatever is stored.
 *
 * Mirror of electron-react-boilerplate/src/shared/stockLedger.ts
 * `collapseRecipe`, which the till uses for the same reason — and where a
 * duplicate also made the fan-out consume half what it should.
 * See docs/stock-test-findings.md H2.
 */
export function collapseRecipe(lines: RecipeLine[] | null | undefined): RecipeLine[] {
  const out: RecipeLine[] = [];
  const seen: Record<string, RecipeLine> = {};

  for (const line of Array.isArray(lines) ? lines : []) {
    const stockId = line?.stockId;
    const qty = num(line?.qtyPerPortion);
    if (!stockId || !(qty > 0)) continue;

    if (seen[stockId]) {
      seen[stockId].qtyPerPortion = round6(seen[stockId].qtyPerPortion + qty);
      continue;
    }
    const copy = {...line, stockId, qtyPerPortion: qty};
    seen[stockId] = copy;
    out.push(copy);
  }

  return out;
}

/**
 * What the whole recipe allows, and which ingredient runs out first.
 *
 * `tracked: false` means no line has a baseline — the dish then behaves as
 * Not tracked rather than Sold out, so switching recipe mode on cannot empty
 * a menu before anyone has weighed anything.
 */
export function recipePreview(
  lines: RecipeLine[],
  ingredientsById: Record<string, IngredientLike>,
): RecipePreview {
  let portions: number | null = null;
  let limitingStockId: string | null = null;

  for (const line of collapseRecipe(lines)) {
    const allowed = portionsForLine(line, ingredientsById[line?.stockId]);
    if (allowed === null) continue;
    if (portions === null || allowed < portions) {
      portions = allowed;
      limitingStockId = line.stockId;
    }
  }

  if (portions === null) {
    return {tracked: false, portions: 0, limitingStockId: null, limitingName: null};
  }
  return {
    tracked: true,
    portions,
    limitingStockId,
    limitingName: ingredientsById[limitingStockId!]?.itemName ?? null,
  };
}

export interface RecipeProblem {
  index: number;
  error: string;
}

/**
 * Shape checks before a recipe may be saved.
 *
 * A duplicate ingredient is the one that matters: two lines for rice would
 * both fan out on every sale, so the dish would consume twice what the chef
 * actually uses.
 */
export function validateRecipe(lines: RecipeLine[]): RecipeProblem[] {
  const problems: RecipeProblem[] = [];
  const list = lines ?? [];

  if (list.length === 0) {
    return [{index: -1, error: 'A recipe needs at least one ingredient.'}];
  }

  const seen = new Set<string>();
  list.forEach((line, index) => {
    if (!line?.stockId) {
      problems.push({index, error: 'Choose an ingredient.'});
      return;
    }
    if (seen.has(line.stockId)) {
      problems.push({index, error: 'This ingredient is already in the recipe.'});
    }
    seen.add(line.stockId);
    if (!(num(line.qtyPerPortion) > 0)) {
      problems.push({index, error: 'Quantity must be more than 0.'});
    }
  });

  return problems;
}
